export const GAME_CONSTANTS = {
  // Time
  GAME_START_DATE: '2024-01-01T00:00:00',
  TICK_INTERVAL_MS: 100,
  MINUTES_PER_TICK: 10,

  // Economy
  FUEL_BASE_PRICE: 500,
  FUEL_PRICE_VARIANCE: 0.3,
  MARKET_UPDATE_INTERVAL_HOURS: 6,
  CONTRACT_REFRESH_INTERVAL_HOURS: 12,

  // Ships
  SHIP_DEPRECIATION_RATE: 0.001,
  MIN_SHIP_VALUE_PERCENT: 0.2,
  REPAIR_COST_PER_PERCENT: 100,

  // Contracts
  CONTRACT_LATE_PENALTY_PERCENT: 10,
  CONTRACT_EARLY_BONUS_PERCENT: 5,
  MIN_CONTRACTS_AVAILABLE: 20,
  MAX_CONTRACTS_AVAILABLE: 50,
  CONTRACT_LOCALITY_BIAS: 0.6,
  CONTRACT_LOCALITY_RANGE_NM: 2000,

  // Berthing fees (daily, after 1-day grace period)
  BERTHING_FEES: {
    tiny:   1000,
    small:  3000,
    medium: 10000,
    large:  30000,
    major:  80000
  },

  // Cargo handling (loading/unloading) — crane throughput in TEU per game
  // hour by port size, plus a fixed setup time (mooring the cranes / rigging
  // hoses) at the start of each handling session. See Ship.estimateHandlingHours().
  CARGO_HANDLING_RATES: {
    tiny:   12,
    small:  30,
    medium: 75,
    large:  180,
    major:  400
  },
  CARGO_HANDLING_SETUP_HOURS: 1,

  // Gameplay
  FUEL_WARNING_THRESHOLD: 0.2,
  CONDITION_WARNING_THRESHOLD: 30,
  BERTH_OVERAGE_MULTIPLIER: 1.5,

  // UI
  AUTOSAVE_INTERVAL_MINUTES: 5,
  MAX_NOTIFICATION_QUEUE: 10,
  NOTIFICATION_DURATION_MS: 5000,

  // Analytics
  ANALYTICS_SNAPSHOT_INTERVAL_SECONDS: 300,
  ANALYTICS_ENDPOINT: '/api/analytics',
  ANALYTICS_ENABLED_DEFAULT: true
};

export const TIME_CONFIG = {
  TICK_INTERVAL_MS: 100,
  GAME_MINUTES_PER_TICK: 10,
  SPEEDS: [0, 1, 2, 5, 10],
  DEFAULT_SPEED: 1
};

export const DIFFICULTY_LEVELS = {
  easy:   { label: 'Easy',   startingMoney: 200000, contractCount: 1.5, operatingCosts: 0.75, fuelConsumption: 0.85, conditionWear: 0.75, cargoSpoilage: 0.75, contractDeadline: 1.3, contractPayment: 1.2 },
  normal: { label: 'Normal', startingMoney: 100000, contractCount: 1.0, operatingCosts: 1.0,  fuelConsumption: 1.0,  conditionWear: 1.0,  cargoSpoilage: 1.0,  contractDeadline: 1.0, contractPayment: 1.0 },
  hard:   { label: 'Hard',   startingMoney: 50000,  contractCount: 0.7, operatingCosts: 1.5,  fuelConsumption: 1.25, conditionWear: 1.5,  cargoSpoilage: 1.5,  contractDeadline: 0.75, contractPayment: 0.8 }
};

export const CREW_POOL_CONFIG = {
  POOL_SIZE: {
    tiny:   { min: 0, max: 1 },
    small:  { min: 0, max: 2 },
    medium: { min: 1, max: 3 },
    large:  { min: 2, max: 5 },
    major:  { min: 3, max: 8 }
  },
  // Target pool size the system gravitates toward
  EQUILIBRIUM: {
    tiny:   1,
    small:  1,
    medium: 2,
    large:  3,
    major:  5
  },
  TIER_WEIGHTS: {
    tiny:   { junior: 0.90, senior: 0.10, expert: 0.00 },
    small:  { junior: 0.75, senior: 0.22, expert: 0.03 },
    medium: { junior: 0.60, senior: 0.30, expert: 0.10 },
    large:  { junior: 0.45, senior: 0.40, expert: 0.15 },
    major:  { junior: 0.35, senior: 0.40, expert: 0.25 }
  },
  ROLE_WEIGHTS: {
    tiny:   { navigator: 0.50, engineer: 0.50, market_analyst: 0.00 },
    small:  { navigator: 0.45, engineer: 0.45, market_analyst: 0.10 },
    medium: { navigator: 0.40, engineer: 0.40, market_analyst: 0.20 },
    large:  { navigator: 0.38, engineer: 0.38, market_analyst: 0.24 },
    major:  { navigator: 0.35, engineer: 0.35, market_analyst: 0.30 }
  },
  DAILY_TRICKLE_CHANCE: {
    tiny:   0.05,
    small:  0.08,
    medium: 0.12,
    large:  0.20,
    major:  0.30
  },
  // Base daily chance of a crew member departing when pool is above equilibrium
  DEPARTURE_BASE_CHANCE: 0.10,
  CONTRACT_MIN_DAYS: 365,
  CONTRACT_PENALTY_RATE: 0.5,
  WEALTH_BONUS_DIVISOR: 5
};

export const MILESTONES = [
  { id: 'first_delivery',   name: 'First Delivery',      description: 'Complete your first cargo delivery',   condition: { type: 'deliveries',  value: 1    }, reward: 10000  },
  { id: 'fleet_commander',  name: 'Fleet Commander',     description: 'Own 5 ships at the same time',          condition: { type: 'ships',       value: 5    }, reward: 50000  },
  { id: 'millionaire',      name: 'Millionaire',         description: 'Reach $1,000,000 cash on hand',         condition: { type: 'balance',     value: 1000000 }, reward: 100000 },
  { id: 'global_trader',    name: 'Global Trader',       description: 'Visit 20 different ports',              condition: { type: 'portsVisited',value: 20   }, reward: 200000 },
  { id: 'tycoon',           name: 'Tycoon',              description: 'Reach $10,000,000 net worth',           condition: { type: 'netWorth',    value: 10000000 }, reward: 500000 },
  { id: 'magnate',          name: 'Shipping Magnate',    description: 'Reach $100,000,000 net worth',          condition: { type: 'netWorth',    value: 100000000 }, reward: 1000000 }
];

// Default company outline colours shown on the map (own ships' outline,
// other players' ships in the vesselfinder-style world view). A new player
// is assigned one of these deterministically (server/game/bootstrap.js); the
// settings menu lets a player pick any of these to change it later.
export const COMPANY_COLOR_PALETTE = [
  '#00bcd4', // cyan
  '#9b59b6', // purple
  '#e91e63', // pink
  '#ff9800', // orange
  '#2ecc71', // green
  '#3498db', // blue
  '#f1c40f', // yellow
  '#e74c3c'  // red
];

/** Validates the `#rrggbb` hex strings companyColor is always stored as. */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Matches SHIP_NAME_MAX_LENGTH's role in server/transport/intents/ship.js. */
export const COMPANY_NAME_MAX_LENGTH = 40;
